import { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { api } from "../api/client";
import type { LeadDetail } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useMutation, useResource } from "../hooks/useResource";
import {
  ErrorBanner,
  LoadingState,
  Mono,
  PageHeader,
  QualityChip,
  RoleGate,
  when,
} from "../components/common";
import { MONO } from "../theme";

/**
 * One lead, with everything needed to make the call and record what happened.
 *
 * This screen is the direct replacement for scrolling back through a Slack thread:
 * the reason, the evidence, who to call, and the outcome control in one place.
 */

const FUNNEL = ["delivered", "contacted", "quoted", "won", "lost", "bad_lead"];

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const detail = useResource<LeadDetail>(id ? `/api/leads/${id}` : null);

  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [value, setValue] = useState("");

  // Seed the form from whatever was last reported, so re-reporting is an edit
  // rather than a blank slate the operator has to reconstruct.
  useEffect(() => {
    if (!detail.data) return;
    setOutcome(detail.data.outcome?.outcome ?? "");
    setNote(detail.data.outcome?.note ?? "");
    setValue(detail.data.outcome?.valueUsd != null ? String(detail.data.outcome.valueUsd) : "");
  }, [detail.data]);

  const report = useMutation(async () => {
    await api.post(`/api/leads/${id}/outcome`, {
      outcome,
      note: note.trim() === "" ? undefined : note.trim(),
      valueUsd: value.trim() === "" ? undefined : Number(value),
    });
  });

  if (detail.loading && !detail.data) return <LoadingState label="Loading lead…" />;
  if (detail.error) return <ErrorBanner error={detail.error} onRetry={detail.reload} />;
  if (!detail.data) return null;

  const { lead, property, organization, contacts, assignment, deliveries, outcome: recorded } = detail.data;
  const payload = lead.payload as {
    whyNow?: string | null;
    event?: Record<string, unknown>;
    organization?: { portfolioBuildings?: number };
  };
  const evidence = payload.event ?? {};
  const sourceUrl = typeof evidence["sourceUrl"] === "string" ? evidence["sourceUrl"] : null;
  const emails = contacts.filter((c) => c.kind === "email");
  const phones = contacts.filter((c) => c.kind === "phone");

  return (
    <Box>
      <PageHeader
        title={property?.address ?? lead.signalKey}
        subtitle={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <QualityChip quality={lead.quality} />
            <Chip label={lead.marketKey ?? "no market"} variant="outlined" />
            <Chip label={lead.signalLabel ?? lead.signalKey} variant="outlined" />
            <Chip
              label={lead.signalKind === "event" ? "event" : "stock"}
              variant="outlined"
              color={lead.signalKind === "event" ? "primary" : "default"}
            />
          </Stack>
        }
        actions={
          <Button component={RouterLink} to="/leads" startIcon={<ArrowBackIcon />}>
            Back to leads
          </Button>
        }
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2 }}>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Why now
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {payload.whyNow ?? "No reason sentence — this signal has no whyNow template configured."}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.5 }}>
              <Field label="Event date" value={lead.eventAt ? lead.eventAt.slice(0, 10) : "—"} />
              <Field label="Found" value={when(lead.createdAt)} />
              <Field label="Property kind" value={property?.kind ?? "—"} />
              <Field label="Units" value={attr(property?.attrs, "resUnits") ?? "—"} />
              <Field label="Year built" value={attr(property?.attrs, "yearBuilt") ?? "—"} />
              <Field
                label="Payee portfolio"
                value={
                  payload.organization?.portfolioBuildings != null
                    ? `${payload.organization.portfolioBuildings} buildings`
                    : "—"
                }
              />
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Official record
            </Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableBody>
                {[
                  ["Source", evidence["source"]],
                  ["Reference", evidence["reference"]],
                  ["Status", evidence["status"]],
                  ["Severity", evidence["severity"]],
                  ["Description", evidence["description"]],
                  ["Deadline", evidence["deadline"]],
                  ["Balance due", evidence["balanceDue"]],
                ].map(([label, raw]) => (
                  <TableRow key={String(label)}>
                    <TableCell sx={{ width: 140, color: "text.secondary", border: 0 }}>{String(label)}</TableCell>
                    <TableCell sx={{ border: 0 }}>{raw == null || raw === "" ? "—" : String(raw)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {sourceUrl ? (
              <Link href={sourceUrl} target="_blank" rel="noreferrer noopener" variant="body2">
                Open the source dataset
              </Link>
            ) : null}
            {lead.sourceNaturalKey ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Raw record key <Mono>{lead.sourceNaturalKey}</Mono>, observed {when(lead.observedAt)}
              </Typography>
            ) : null}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Who to call
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              {organization?.name ?? "No payee resolved"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {organization?.kind ?? "—"}
              {attr(organization?.attrs, "domain") ? ` · ${attr(organization?.attrs, "domain")}` : ""}
            </Typography>

            {contacts.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No contacts yet. This self-heals on the next enrichment run.
              </Alert>
            ) : (
              <Table size="small" sx={{ mt: 1.5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Contact</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Confidence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...emails, ...phones].map((c) => (
                    <TableRow key={`${c.kind}-${c.value}`}>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Link
                            href={c.kind === "email" ? `mailto:${c.value}` : `tel:${c.value}`}
                            variant="body2"
                            sx={{ fontFamily: MONO }}
                          >
                            {c.value}
                          </Link>
                          {c.sourceProvider?.endsWith("_unverified") ? (
                            <Tooltip title="Matched by company name, not the owner's own domain. Confirm before mailing.">
                              <Chip label="guessed" color="warning" variant="outlined" />
                            </Tooltip>
                          ) : null}
                          {c.verification && c.verification !== "deliverable" ? (
                            <Tooltip title="The provider's own deliverability verdict.">
                              <Chip
                                label={c.verification}
                                color={c.verification === "undeliverable" ? "error" : "warning"}
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{c.title ?? "—"}</TableCell>
                      <TableCell>{c.confidence != null ? `${Math.round(c.confidence * 100)}%` : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>

          <Accordion variant="outlined" disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">Full lead payload (as delivered)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{ m: 0, fontFamily: MONO, fontSize: 12, overflow: "auto", maxHeight: 420 }}
              >
                {JSON.stringify(lead.payload, null, 2)}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Stack>

        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Routing
            </Typography>
            {assignment ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body1">
                  <Link component={RouterLink} to={`/clients/${assignment.clientId}`}>
                    {assignment.clientName}
                  </Link>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {assignment.strategy} · {when(assignment.assignedAt)}
                </Typography>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Held — not assigned to a client. On a geo-exclusive market this means nobody owns this
                territory, or the owner is over quota.
              </Alert>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Record the outcome
            </Typography>
            {recorded ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Last reported {when(recorded.reportedAt)}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Nothing reported yet.
              </Typography>
            )}

            <ErrorBanner error={report.error} />

            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                label="Outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                fullWidth
                disabled={!assignment}
              >
                {FUNNEL.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                disabled={!assignment}
                helperText="The client's own words — why it was bad, what the job was worth."
              />
              <TextField
                label="Job value (USD)"
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/[^0-9.]/g, ""))}
                fullWidth
                disabled={!assignment || outcome !== "won"}
                helperText={outcome === "won" ? "Makes lead → revenue measurable." : "Only for a won job."}
              />
              <RoleGate allowed={can("operator")} needs="operator">
                <Button
                  variant="contained"
                  onClick={() => void report.run(undefined).then((ok) => ok && detail.reload())}
                  disabled={!assignment || outcome === "" || report.pending}
                  fullWidth
                >
                  {report.pending ? "Saving…" : "Save outcome"}
                </Button>
              </RoleGate>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Delivery attempts
            </Typography>
            {deliveries.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Never sent.
              </Typography>
            ) : (
              <Table size="small" sx={{ mt: 1 }}>
                <TableBody>
                  {deliveries.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell sx={{ border: 0 }}>
                        <Chip
                          label={d.status}
                          color={d.status === "ok" ? "success" : "error"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ border: 0 }}>{d.channelType ?? "—"}</TableCell>
                      <TableCell sx={{ border: 0 }}>#{d.attempt}</TableCell>
                      <TableCell sx={{ border: 0 }}>{when(d.at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function attr(attrs: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = attrs?.[key];
  return typeof value === "string" && value !== "" ? value : typeof value === "number" ? String(value) : null;
}
